import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-6 py-10 sm:py-16 text-[#1a1a1a]">
      {/* Title */}
      <h1 className="text-[32px] sm:text-4xl md:text-5xl font-medium mb-5 sm:mb-8 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
        Privacy Policy
      </h1>

      <p className="text-sm mb-7 sm:mb-10 opacity-70">Last updated: October 6, 2025</p>

      <div className="prose prose-lg max-w-none text-[15px] sm:text-[16px] leading-relaxed space-y-7 sm:space-y-8">

        <p>
          Aishcraft operates this store and website, including all related information, content, features,
          tools, products and services, in order to provide you, the customer, with a curated shopping
          experience (the "Services"). We are committed to protecting your personal information and your
          right to privacy. If you have any questions or concerns about our policy or our practices with
          regards to your personal information, please contact us at hello@aishcraft.com.
        </p>

        <p>
          When you visit our website and use our services, you trust us with your personal information.
          We take your privacy very seriously. In this privacy notice, we describe our privacy policy.
          We seek to explain to you in the clearest way possible what information we collect, how we use
          it and what rights you have in relation to it. We hope you take some time to read through it
          carefully, as it is important.
        </p>

        {/* 1 */}
        <Section title="Personal Information We Collect or Process">
          <p>
            We may collect or process various categories of personal information about you, including but
            not limited to:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li><strong>Contact Details:</strong> Name, email address, phone number, billing and shipping address.</li>
            <li><strong>Financial Information:</strong> Payment card details, bank account information (processed securely via our payment partners and never stored directly by us).</li>
            <li><strong>Account Information:</strong> Username, password, account preferences, and wishlist data.</li>
            <li><strong>Transaction Information:</strong> Details about purchases, returns, or exchanges you make with us.</li>
            <li><strong>Communications:</strong> Messages you send us via email, live chat, or social media.</li>
            <li><strong>Device & Usage Information:</strong> IP address, browser type, pages visited, time spent on pages, and referring URLs.</li>
            <li><strong>Location Information:</strong> General geographic location based on your IP address or precise location if you grant permission.</li>
          </ul>
        </Section>

        {/* 2 */}
        <Section title="Personal Information Sources">
          <p>We collect personal information from the following sources:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li><strong>Directly from you:</strong> When you create an account, place an order, subscribe to our newsletter, contact our support team, or participate in surveys and promotions.</li>
            <li><strong>Automatically:</strong> Through cookies, web beacons, and similar tracking technologies when you browse our website.</li>
            <li><strong>Service Providers:</strong> Analytics platforms, payment processors, and shipping partners who share information with us as part of delivering our services.</li>
            <li><strong>Third-Party Partners:</strong> Social media platforms, advertising networks, and affiliate partners when you interact with our content outside our website.</li>
          </ul>
        </Section>

        {/* 3 */}
        <Section title="How We Use Your Personal Information">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Provide, Tailor, and Improve the Services:</strong> To process payments, fulfil orders, manage returns, and create a customised shopping experience tailored to your preferences.</li>
            <li><strong>Marketing and Advertising:</strong> To send promotional communications, special offers, and show online advertisements based on your browsing and purchase activity. You may opt out at any time.</li>
            <li><strong>Security and Fraud Prevention:</strong> To authenticate accounts, monitor suspicious activity, and detect or prevent fraudulent or malicious behaviour.</li>
            <li><strong>Communicating with You:</strong> To provide customer support, send order confirmations, shipping updates, and maintain our business relationship with you.</li>
            <li><strong>Analytics and Research:</strong> To understand how customers use our website and services, so we can improve our offerings and user experience.</li>
            <li><strong>Legal Reasons:</strong> To comply with applicable laws, regulations, or valid legal processes, and to protect the rights, property, or safety of Aishcraft, our customers, or others.</li>
          </ul>
        </Section>

        {/* 4 */}
        <Section title="Cookies and Tracking Technologies">
          <p>
            We use cookies and similar tracking technologies to collect and store information when you
            interact with our website. Cookies are small data files placed on your device that help us
            improve your experience.
          </p>
          <p className="mt-3">We use the following types of cookies:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li><strong>Strictly Necessary Cookies:</strong> Essential for the website to function, such as keeping items in your cart.</li>
            <li><strong>Performance Cookies:</strong> Help us understand how visitors interact with our website by collecting anonymous data.</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences such as language, currency, or login details.</li>
            <li><strong>Targeting/Advertising Cookies:</strong> Used to deliver relevant ads and track the effectiveness of our marketing campaigns.</li>
          </ul>
          <p className="mt-3">
            You can control cookies through your browser settings. Disabling certain cookies may affect
            the functionality of our website.
          </p>
        </Section>

        {/* 5 */}
        <Section title="How We Share Your Personal Information">
          <p>
            We do not sell your personal information. We may share your information with trusted third
            parties only in the following circumstances:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li><strong>Service Providers:</strong> Payment processors, shipping carriers, email marketing platforms, and analytics providers who assist us in operating our business.</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
            <li><strong>Legal Compliance:</strong> When required by law, court order, or governmental authority.</li>
            <li><strong>Protection of Rights:</strong> To enforce our Terms of Service or protect the rights, property, or safety of Aishcraft and our customers.</li>
          </ul>
        </Section>

        {/* 6 */}
        <Section title="Data Retention">
          <p>
            We retain your personal information for as long as necessary to fulfil the purposes outlined
            in this privacy policy, unless a longer retention period is required or permitted by law.
            Specifically:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>Order and transaction records are retained for a minimum of 7 years for tax and accounting purposes.</li>
            <li>Account information is retained for as long as your account is active or as needed to provide services.</li>
            <li>Marketing preferences and communications are retained until you opt out or request deletion.</li>
          </ul>
          <p className="mt-3">
            When personal information is no longer necessary, we will securely delete or anonymise it.
          </p>
        </Section>

        {/* 7 */}
        <Section title="Your Rights and Choices">
          <p>
            Depending on where you live, you may have certain rights regarding your personal information.
            These may include:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li><strong>Right to Access:</strong> Request a copy of the personal information we hold about you.</li>
            <li><strong>Right to Correction:</strong> Request that we correct inaccurate or incomplete information.</li>
            <li><strong>Right to Deletion:</strong> Request that we delete your personal information, subject to certain legal obligations.</li>
            <li><strong>Right to Portability:</strong> Request that your data be transferred to you or another service provider in a structured, machine-readable format.</li>
            <li><strong>Right to Object:</strong> Object to the processing of your data for marketing purposes at any time.</li>
            <li><strong>Right to Withdraw Consent:</strong> If processing is based on consent, you may withdraw it at any time without affecting prior processing.</li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, please contact us at hello@aishcraft.com. We will respond
            to your request within 30 days.
          </p>
        </Section>

        {/* 8 */}
        <Section title="Security of Your Information">
          <p>
            We use administrative, technical, and physical security measures to protect your personal
            information. All payment transactions are encrypted using SSL technology. While we have taken
            reasonable steps to secure the personal information you provide to us, please be aware that
            no security measures are perfect or impenetrable, and no method of data transmission can be
            guaranteed against any interception or other type of misuse.
          </p>
          <p className="mt-3">
            We strongly advise you to use a strong, unique password for your Aishcraft account and to
            never share it with anyone.
          </p>
        </Section>

        {/* 9 */}
        <Section title="Children's Privacy">
          <p>
            Our Services are not directed to individuals under the age of 13. We do not knowingly collect
            personal information from children under 13. If we become aware that a child under 13 has
            provided us with personal information, we will take steps to delete such information. If you
            believe we might have any information from or about a child under 13, please contact us at
            hello@aishcraft.com.
          </p>
        </Section>

        {/* 10 */}
        <Section title="Changes to This Privacy Policy">
          <p>
            We may update this privacy policy from time to time in order to reflect changes to our
            practices or for other operational, legal, or regulatory reasons. We will notify you of any
            material changes by posting the new privacy policy on this page and updating the "Last updated"
            date at the top of this policy.
          </p>
          <p className="mt-3">
            We encourage you to review this privacy policy periodically to stay informed about how we are
            protecting your information. Your continued use of our Services after any changes constitutes
            your acceptance of the updated policy.
          </p>
        </Section>

        {/* 11 */}
        <Section title="Contact">
          <p>
            Should you have any questions, concerns, or requests regarding this privacy policy or our data
            practices, please do not hesitate to reach out to us. We are happy to help.
          </p>
          <ul className="list-none pl-0 space-y-1 mt-3">
            <li><strong>Email:</strong> hello@aishcraft.com</li>
            <li><strong>Address:</strong> Aishcraft, Bhopal, Madhya Pradesh, India</li>
          </ul>
        </Section>

      </div>
    </div>
  );
};

// Helper component for sections
const Section = ({ title, children }) => (
  <div>
    <h2 className="text-[22px] sm:text-2xl md:text-3xl font-medium mb-3 sm:mb-4 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
      {title}
    </h2>
    {children}
  </div>
);

export default PrivacyPolicy;